import { createUploadAuth, encodeAuthorizationHeader } from 'blossom-client-sdk';
import { signEvent } from './auth';
import { SERVER } from './stores';

export async function computeSha256(data: Blob | File): Promise<string> {
  const buffer = await data.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

export async function uploadFile(
  file: File,
  cashuToken?: string,
  onProgress?: (percent: number) => void,
): Promise<unknown> {
  const sha256 = await computeSha256(file);
  const auth = await createUploadAuth(
    (e) => signEvent(e),
    sha256,
    { type: 'upload', expiration: Math.floor(Date.now() / 1000) + 300 }
  );
  const headers: Record<string, string> = {
    'Authorization': encodeAuthorizationHeader(auth),
    'Content-Type': file.type || 'application/octet-stream',
  };
  if (cashuToken) {
    headers['X-Cashu'] = cashuToken;
  }

  if (onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', `${SERVER}/upload`);

      for (const [key, value] of Object.entries(headers)) {
        xhr.setRequestHeader(key, value);
      }

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { resolve({}); }
        } else {
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.responseText}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(file);
    });
  }

  const res = await fetch(`${SERVER}/upload`, {
    method: 'PUT',
    body: file,
    headers,
  });
  if (!res.ok) throw new Error(`Upload failed: ${res.status} ${await res.text()}`);
  return res.json();
}

export type VideoMode = 'thumbnail' | 'transcode';

/**
 * Upload to the /media endpoint for server-side optimization.
 * Always requires Cashu payment (no free tier).
 * For video, `videoMode` selects thumbnail-only (21 sats) or full transcode (21 sats/MB).
 */
export async function uploadMedia(
  file: File,
  cashuToken: string,
  videoMode: VideoMode = 'thumbnail',
  onProgress?: (percent: number) => void,
): Promise<unknown> {
  const sha256 = await computeSha256(file);
  const auth = await createUploadAuth(
    (e) => signEvent(e),
    sha256,
    { type: 'upload', expiration: Math.floor(Date.now() / 1000) + 300 }
  );
  const headers: Record<string, string> = {
    'Authorization': encodeAuthorizationHeader(auth),
    'Content-Type': file.type || 'application/octet-stream',
    'X-Cashu': cashuToken,
  };
  if (file.type.startsWith('video/')) {
    headers['X-Video-Mode'] = videoMode;
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `${SERVER}/media`);

    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value);
    }

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch { resolve({}); }
      } else {
        reject(new Error(`Media upload failed: ${xhr.status} ${xhr.responseText}`));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during media upload'));
    xhr.send(file);
  });
}

export async function listFiles(pubkey: string): Promise<unknown[]> {
  const res = await fetch(`${SERVER}/list/${pubkey}`);
  if (!res.ok) return [];
  return res.json();
}
